import { useState, type ReactNode } from "react";
import { LIBRARY } from "../design/copy.js";
import { CaseKindRow, type CardGlyph } from "./case-card.js";
import { DRAG_Z, LIGHTBOX_MARGIN_REM, LIGHTBOX_W_REM } from "./case-lightbox.js";

/** The tall paper's height in rem, the same figure as app.css `.pc-desk-lightbox-tall`. */
export const TALL_H_REM = 44;
import { remBase, usePointerDrag } from "./use-drag.js";

/**
 * THE DESK'S OTHER PAPERS, PICKED UP (2026-09-23, Andre): the focus and the
 * practice exercise live on the desk as cards in the case card's design, and
 * open into the case lightbox's design — the same paper, bar, kind row and
 * "put it down.", the same drag by the bar, the same one-at-a-time rule.
 * What differs is inside: a scrolling body that holds however much a
 * practice session needs, and a FOOT pinned under it for the composer, so
 * the paper keeps roughly the size of an expanded case card while the
 * session inside it grows.
 */
export function DeskLightbox({
  kindWords,
  glyph,
  ariaLabel,
  pos,
  leaving,
  tall = false,
  onMove,
  onClose,
  children,
  foot = null,
  error = null,
  closeLabel = LIBRARY.putDown,
}: {
  kindWords: string;
  glyph: CardGlyph;
  ariaLabel: string;
  pos: { readonly x: number; readonly y: number };
  leaving: boolean;
  /** A fixed height (a practice session), so the foot stays put and the body scrolls. */
  tall?: boolean | undefined;
  onMove: (pos: { readonly x: number; readonly y: number }) => void;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
  /** A refusal from the last thing done on this paper, in the server's words. */
  error?: string | null | undefined;
  /** The bar's word for putting the paper down; the email card's is "collapse card." (Codex, 2026-10-07). */
  closeLabel?: string | undefined;
}): ReactNode {
  const [delta, setDelta] = useState<{ x: number; y: number } | null>(null);
  const handle = usePointerDrag({
    onMove: (dx, dy) => { setDelta({ x: dx, y: dy }); },
    onEnd: (end) => {
      setDelta(null);
      if (end.moved) onMove({ x: pos.x + end.dx, y: pos.y + end.dy });
    },
  });
  const base = remBase();
  const width = Math.min(LIGHTBOX_W_REM * base, globalThis.innerWidth - 2 * LIGHTBOX_MARGIN_REM * base);
  const x = Math.min(Math.max(pos.x + (delta?.x ?? 0), 0), Math.max(globalThis.innerWidth - width, 0));
  // A tall paper has a known height (app.css .pc-desk-lightbox-tall); it lands beside its card like
  // any other, but never with its foot below the window, or the composer is out of reach (live,
  // 2026-09-23). The case lightbox is content-sized and needs no such clamp.
  const tallHeight = tall ? Math.min(TALL_H_REM * base, globalThis.innerHeight - 2 * LIGHTBOX_MARGIN_REM * base) : null;
  const yCeiling = tallHeight === null ? Number.POSITIVE_INFINITY : Math.max(globalThis.innerHeight - tallHeight - LIGHTBOX_MARGIN_REM * base, 0);
  const y = Math.min(Math.max(pos.y + (delta?.y ?? 0), 0), yCeiling);

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel}
      aria-hidden={leaving ? "true" : undefined}
      data-lightbox={glyph}
      className={`pc-case-lightbox fixed flex max-h-[calc(100svh-2rem)] flex-col overflow-hidden ${tall ? "pc-desk-lightbox-tall" : ""} ${leaving ? "pc-lightbox-out" : "pc-lightbox-in"}`}
      style={{ left: `${String(x)}px`, top: `${String(y)}px`, width: `${String(width)}px`, zIndex: leaving ? DRAG_Z + 1 : DRAG_Z + 2 }}
    >
      <header onPointerDown={leaving ? undefined : handle} className="pc-case-lightbox-bar select-none">
        <div className="pc-case-lightbox-kind">
          <CaseKindRow words={kindWords} glyph={glyph} />
        </div>
        <button type="button" onClick={onClose} aria-label={closeLabel === LIBRARY.putDown ? "Put this down" : closeLabel}>
          {closeLabel}
        </button>
      </header>
      <div className="pc-scroll pc-case-lightbox-body pc-desk-lightbox-body">{children}</div>
      {error === null ? null : <p role="status" className="pc-desk-lightbox-error">{error}</p>}
      {foot === null ? null : <div className="pc-desk-lightbox-foot">{foot}</div>}
    </section>
  );
}
